import { ChangeDetectionStrategy, Component, input, model } from '@angular/core';
import { FormCheckboxControl } from '@angular/forms/signals';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { SignalFieldUiDirective } from '../signal-field-base.directive';
import { getUuidV4 } from '../../utils/uuid.utils';

/**
 * Low-level checkbox for signal forms.
 *
 * The only field that does not edit a `value`: signal forms has a second control
 * contract for checkboxes, whose required member is `checked` and which forbids
 * `value` outright. So this extends `SignalFieldUiDirective` — the shared half that
 * carries no value member — rather than `SignalFieldBaseDirective`:
 *
 * ```html
 * <pace-core-checkbox-field [formField]="signalForm.accepted" label="Travel billable" />
 * ```
 *
 * `required` on a boolean path is ambiguous (is `false` a value or the absence of
 * one?), so a "must be ticked" rule belongs in the owning form's schema as an
 * explicit check rather than being inferred here.
 */
@Component({
    selector: 'pace-checkbox-field',
    templateUrl: './checkbox-field.component.html',
    styleUrls: ['./checkbox-field.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FaIconComponent],
})
export class CheckboxFieldComponent extends SignalFieldUiDirective implements FormCheckboxControl {
    /** the tick state; kept in sync with the bound field by the `[formField]` directive */
    public readonly checked = model(false);

    /** optional caption rendered next to the box, replacing the legacy `DisplayDecorator.displayName` */
    public readonly label = input('');

    /**
     * Ties the caption to the box. Generated per instance so consumers get working
     * label association without having to invent unique ids.
     */
    public readonly fieldId = input(`checkbox-field-${getUuidV4()}`);

    /** dims the control and blocks pointer events, as `isReadonlyInputControl` does in the legacy widget */
    public readonly dimmed = input(false);

    public readonly faCheck = faCheck;

    // A native checkbox has no readonly state, so the template reaches for `editable`
    // rather than `readonly`: both flags have to end up on the `disabled` attribute, or a
    // readonly field would still be togglable.

    public onChange(event: Event): void {
        this.checked.set((<HTMLInputElement>event.target).checked);
        this.touched.set(true);
    }
}
